import { Link } from 'react-router';
import { canReport } from '../lib/contact';
import { InfoPage, InfoSection } from './InfoPage';

/**
 * What the product actually does for safety, and what it does not.
 *
 * Written against the build rather than against an aspiration: every feature
 * named here exists, and the "not yet" section names the ones people will
 * reasonably assume are present because most marketplaces have them.
 */
export function SafetyScreen() {
  return (
    <InfoPage
      title="Safety"
      standfirst="Sharing a car with a stranger asks for some trust. These are the things OpenRoad gives you to place it well — and the things it does not do, which matter just as much."
    >
      <InfoSection heading="Before you book">
        <p className="m-0">
          Every driver's profile carries their rating, how many reviews it is built from, how long
          they have been a member, and how often they answer messages. A driver with no reviews is
          not a bad driver, but they are an unknown one — and the profile says which you are looking
          at rather than rounding it up.
        </p>
        <p className="m-0">
          Read <Link to="/verification">how verification works</Link> before you weigh a badge. On
          this deployment they are placeholders, and a badge should not move your decision.
        </p>
      </InfoSection>

      <InfoSection heading="Messages stay here">
        <p className="m-0">
          You can message a driver from the ride, before and after booking, without giving out a
          phone number or an email address. Keeping the conversation on OpenRoad means there is a
          record if something goes wrong.
        </p>
        <p className="m-0">
          Be wary of anyone who wants to move the conversation elsewhere before you have met, and of
          anyone who asks you to pay outside the app. There is no reason to do either.
        </p>
      </InfoSection>

      <InfoSection heading="Rides you can shape">
        <p className="m-0">
          Drivers can mark a ride as women only, and it is shown as such wherever it appears. Rides
          say whether pets, smoking and music are welcome, and how talkative the driver expects to
          be, so an awkward three hours can be avoided before it starts.
        </p>
        <p className="m-0">
          A ride is either instant — the seat is yours when you book — or approved by the driver
          first. Both are labelled. If a driver approves each request, they see who is asking before
          they agree.
        </p>
      </InfoSection>

      <InfoSection heading="What OpenRoad does not do">
        <p className="m-0">Said plainly, because assuming any of these would be worse:</p>
        <ul className="m-0 flex list-disc flex-col gap-1.5 pl-5">
          <li>No background or criminal record checks on anybody.</li>
          <li>No licence or insurance checks on drivers, and no check that a car is warranted.</li>
          <li>No live tracking of a journey, and no emergency button.</li>
          <li>
            No insurance of its own. A trip is covered by whatever the driver's policy covers.
          </li>
        </ul>
        <p className="m-0">
          Tell somebody where you are going and when you expect to arrive. In an emergency, ring 111
          — OpenRoad is not a service that can help you in the moment.
        </p>
      </InfoSection>

      <InfoSection heading="If something goes wrong">
        <p className="m-0">
          If you shared the ride, leave an honest review — it is what the next passenger has to go
          on, and it is on the record.
        </p>
        {canReport ? (
          <p className="m-0">
            For anything that needs a person rather than a rating — including something that
            happened before the ride, or to somebody who was not in the car — the{' '}
            <Link to="/contact">contact page</Link> says where to write. In an emergency, ring 111
            first.
          </p>
        ) : (
          <p className="m-0">
            For anything that needs a person rather than a rating, there is currently nowhere to
            send it. OpenRoad has no reporting channel yet, and a review is not one: it can only be
            written after a ride has happened. The <Link to="/contact">contact page</Link> says the
            same rather than pretending otherwise. In an emergency, ring 111.
          </p>
        )}
      </InfoSection>
    </InfoPage>
  );
}
